import type { PayloadAction, Slice } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import { SessionMentor } from '../../types/mentor'
import type { TranscriptMessage } from '../../types/transcript'

export interface Avatar {
  id: string
  videos: AvatarVideo[]
}

export interface AvatarVideo {
  original_video_url: string
  preview_url?: string
  video_type: string
}

export interface Artifact {
  id: string
  artifactName: string
  iconName: string
  iconDescription: string
}

export interface WebRTCStatus {
  blocked: boolean
  reason?: string
  message?: string
  errorStatus?: number
  btnType?: string
}

export interface SessionState {
  avatarId: string
  dataChannel: RTCDataChannel | null
  sessionId: string | undefined
  avatarReady: boolean
  personaData: SessionMentor | null
  promptData: string | null
  webRTCStatus: WebRTCStatus
  secondsBalance: number
  creditBalance: number
  isSubsDataLoading: boolean
  isFileUploading: boolean
  transcripts: TranscriptMessage[]
  transcript: TranscriptMessage | null
  compositeData: CompositeDataItem[] | null
  voicesList: Voice[] | null
  socketId: string | null
}

export type VisualElement = {
  url: string
  title: string
  previewUrl?: string
}

export type CompositeDataItem = {
  script: string
  visualElements?: VisualElement[]
}

export interface Voice {
  id: string
  provider: string
}

export type CompositeData = CompositeDataItem[]

const initialState: SessionState = {
  avatarId: '',
  dataChannel: null,
  sessionId: undefined,
  avatarReady: false,
  personaData: null,
  promptData: null,
  webRTCStatus: { blocked: false },
  secondsBalance: 0,
  creditBalance: 0,
  isSubsDataLoading: true,
  isFileUploading: false,
  transcripts: [],
  transcript: null,
  voicesList: null,
  compositeData: null,
  socketId: null,
}

export const sessionSlice: Slice<SessionState> = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setAvatarId: (state, action: PayloadAction<string>) => {
      state.avatarId = action.payload
    },
    setDataChannel: (state, action: PayloadAction<RTCDataChannel>) => {
      state.dataChannel = action.payload
    },
    setSessionId: (state, action: PayloadAction<string | undefined>) => {
      state.sessionId = action.payload
    },
    setAvatarReady: (state, action: PayloadAction<boolean>) => {
      state.avatarReady = action.payload
    },
    setPersonaData: (state, action: PayloadAction<SessionMentor | null>) => {
      state.personaData = action.payload
    },
    setPromptData: (state, action: PayloadAction<string>) => {
      state.promptData = action.payload
    },
    setWebRTCStatus: (state, action: PayloadAction<WebRTCStatus>) => {
      state.webRTCStatus = action.payload
    },
    setSecondsBalance: (state, action: PayloadAction<number>) => {
      state.secondsBalance = action.payload
    },
    setCreditBalance: (state, action: PayloadAction<number>) => {
      state.creditBalance = action.payload
    },
    setIsFileUploading: (state, action: PayloadAction<boolean>) => {
      state.isFileUploading = action.payload
    },
    addTranscript: (state, action: PayloadAction<TranscriptMessage>) => {
      state.transcripts.push(action.payload)

      state.transcript = action.payload
    },
    resetTranscript: state => {
      state.transcripts = []
      state.transcript = null
    },
    setCompositeData: (state, action: PayloadAction<CompositeData>) => {
      state.compositeData = action.payload
    },
    setVoicesList: (state, action: PayloadAction<Voice[]>) => {
      state.voicesList = action.payload
    },
    setIsSubsDataLoading: (state, action: PayloadAction<boolean>) => {
      state.isSubsDataLoading = action.payload
    },
    setSocketId: (state, action: PayloadAction<string | null>) => {
      state.socketId = action.payload
    },
  },
})

export const {
  setAvatarId,
  setDataChannel,
  setSessionId,
  setAvatarReady,
  setPersonaData,
  setPromptData,
  setWebRTCStatus,
  setSecondsBalance,
  setCreditBalance,
  isFileUploading,
  setIsFileUploading,
  addTranscript,
  resetTranscript,
  setCompositeData,
  setVoicesList,
  setIsSubsDataLoading,
  setSocketId,
} = sessionSlice.actions

export default sessionSlice.reducer
